import React, { Component, Fragment, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { isEqual } from 'lodash';
import { Pagination, Table } from 'antd';
import { withWindowSize } from 'air-with-window-size';

import './RouteTaskTable.css';

import { EditIcon, DeleteIcon } from '../../../../common/icons'
import FiltersBar from "../FiltersBar";
import AirButton from "../../../../common/components/AirButton";
import AirModalTitlebar from "../../../../common/components/AirModalTitlebar";
import emptyTasksTable from '../../../../common/images/emptyTasksTable.png';
import EditRouteTask from "./EditRouteTask";
import AssignRouteTask from "./AssignRouteTask";
import moment from "moment";

const DeleteTaskButton = inject('translationsStore', 'routeTaskStore')(({ route, translationsStore, routeTaskStore }) => {

  const [visible, setVisible] = useState(false);

  return (
    <Fragment>
      <DeleteIcon onClick={() => setVisible(true)}/>
      <AirModalTitlebar
        className="DeleteRouteTaskModal"
        width='500px'
        visible={visible}
        title={translationsStore.translate('routePlanning.deleteRoute.modal.title')}
        onCancel={() => setVisible(false)}
        cancelButton={<AirButton size="large" label={translationsStore.translate('routePlanning.deleteRoute.modal.cancelButtonLabel')} />}
        okButton={<AirButton size="large" light label={translationsStore.translate('routePlanning.deleteRoute.modal.okButtonLabel')} />}
        onOk={() => {
          routeTaskStore.delete(route.id);
          setVisible(false);
        }}
      >
        <div style={{display:"flex", alignContent:"center", justifyContent:"center", height:'150px'}}>
          <div className={'DeleteRouteModalBody'}>{`${translationsStore.translate('routePlanning.deleteRoute.modal.areYouSure')} "${route.name}"?`}</div>
        </div>
      </AirModalTitlebar>
    </Fragment>
  );
});

const pageSizeDefault = 7;
const pageSizeAlternative = 5;

@inject('commonStore', 'translationsStore', 'routeTaskStore')
@observer
class RouteTaskTable extends Component {
  constructor(props) {
    super(props);
    this.defaultPageSize = 1;
    const dataSource = this.buildDataSourceWithKeys(this.props.routeTaskStore.routeTasks);
    // const pageSize = this.helperPageSize();
    const pageSize = this.defaultPageSize;

    let pagination = false;
    let paginationText = '';
    this.state = {
      dataSourceBuffer: dataSource,
      selectedRowKeys: [],
      pageSize,
      pagination,
      paginationText
    };

    this.EditRouteRef = React.createRef();
  }

  componentDidMount = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.routeTaskStore.routeTasks);
    if (!isEqual(dataSource, this.state.dataSourceBuffer) || (this.props.routeTaskStore.routeTasksTotal > 0 && !this.state.pagination)) {
      this.buildPagination();
    }
    this.EditRouteInstance = this.EditRouteRef.current.wrappedInstance;
  };

  componentDidUpdate(prevProps) {
    const someSizeChanged = prevProps.componentHeight !== this.props.componentHeight || prevProps.windowHeight !== this.props.windowHeight;
    const dataSource = this.buildDataSourceWithKeys(this.props.routeTaskStore.routeTasks);
    if (someSizeChanged || !isEqual(dataSource, this.state.dataSourceBuffer) || (this.props.routeTaskStore.routeTasksTotal > 0 && !this.state.pagination) || (this.props.routeTaskStore.routeTasksTotal !== this.state.pagination.total)) {
      this.buildPagination();
    }
  }

  helperPageSize = () => {
    const defaultDocHeight = 800;
    if (this.props.documentHeight === defaultDocHeight) {
      return !this.props.group ? pageSizeDefault : pageSizeAlternative;
    } else {
      return !this.props.group
        ? Math.floor((this.props.documentHeight * pageSizeDefault) / defaultDocHeight)
        : Math.floor((this.props.documentHeight * pageSizeAlternative) / defaultDocHeight);
    }
  };

  changePageSize = pageSize => {
    this.setState({ pageSize });
  };

  helperPaginationText = (start, end, length) => {
    const { translationsStore } = this.props;
    if (length === 0) return `0 ${translationsStore.translate('routes')}`;
    else if (length === 1) return `1 ${translationsStore.translate('route')}`;
    else return `${start}-${end} ${translationsStore.translate('of')} ${length} ${translationsStore.translate('routes')}`;
  };

  buildDataSourceWithKeys = collection => collection.map((item, index) => ({ ...item, key: index }));

  buildPagination = () => {
    const dataSource = this.buildDataSourceWithKeys(this.props.routeTaskStore.routeTasks);
    const pageSize = this.helperPageSize();
    const pagination = {
      total: this.props.routeTaskStore.routeTasksTotal,
      defaultCurrent: 1,
      current: 1,
      // hideOnSinglePage: true,
      pageSize,
      simple: true,
      size: 'small',
      onChange: this.onPageChange
    };
    const start = 1;
    const end = Math.min(pagination.pageSize, pagination.total);

    const paginationText = this.helperPaginationText(start, end, this.props.routeTaskStore.routeTasksTotal);
    this.setState({
      dataSourceBuffer: dataSource,
      pageSize,
      pagination: { ...pagination },
      paginationText
    });
  };

  onPageChange = (page, pageSize) => {
    this.props.routeTaskStore.load(page,pageSize)
      .then(()=>{
        const dataSource = this.buildDataSourceWithKeys(this.props.routeTaskStore.routeTasks);
        const { pagination } = this.state;
        const start = (page - 1) * pageSize + 1;
        const end = Math.min(page * pageSize, pagination.total);
        let paginationText = this.helperPaginationText(start, end, this.props.routeTaskStore.routeTasksTotal);
        this.setState({
          dataSourceBuffer: dataSource,
          pagination: { ...pagination, current: page },
          paginationText
        });
      });
  };

  render() {
    const {  commonStore, translationsStore } = this.props,
      routes = this.props.routeTaskStore.routeTasks;

    const dataSource = this.buildDataSourceWithKeys(routes);

    const columns = [
      {
        key: 'id',
        dataIndex: 'id',
        className: 'ColumnCentered',
        title: translationsStore.translate('routePlanning.routesTable.headerLabels.number'),
      },
      {
        key: 'name',
        dataIndex: 'name',
        title: translationsStore.translate('routePlanning.routesTable.headerLabels.name'),
      },
      {
        key: 'status',
        dataIndex: 'status',
        title: translationsStore.translate('routePlanning.routesTable.headerLabels.status'),
        render: (status, record) => {
          return translationsStore.translate(`routePlanning.routesTable.statuses.${status}`);
        }
      },
      {
        key: 'date',
        dataIndex: 'date',
        title: translationsStore.translate('routePlanning.routesTable.headerLabels.dateAndHour'),
        render: (status, record) => moment(record.date).add(new Date(record.date).getTimezoneOffset(), 'm').toDate().toLocaleString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      },
      {
        key: 'driver',
        dataIndex: 'driver',
        className: 'ColumnCentered',
        title: translationsStore.translate('routePlanning.routesTable.headerLabels.driver'),
        render: (text, record) => {
          return text ? `${text.surname} ${text.name}` : translationsStore.translate('routePlanning.routesTable.notAssigned')
        }
      },
      {
        key: 'actions',
        dataIndex: 'actions',
        className: 'ColumnCentered',
        title: translationsStore.translate('routePlanning.routesTable.headerLabels.actions'),
        render: (text, record) => {
          return (
            <div className="buttonsContainer">
              <EditIcon onClick={() => record.status === 'not-started' ? this.EditRouteInstance.show(record) : this.setState({visibleCantEdit: true})}/>
              <AssignRouteTask record={record}/>
              {record.status === 'not-started' && <DeleteTaskButton route={record}/>}
            </div>
          );
        }
      }
    ];

    return (
      <div className="RouteTaskTable">
        <FiltersBar />
        {!commonStore.loading && (
          <Fragment>
            <Table columns={columns} dataSource={dataSource} pagination={this.state.pagination} rowClassName="ClientRow" />
            { this.props.routeTaskStore.hasNoTaskYet &&
              <div className={'EmptyTaskImageContainer'}>
                <img src={emptyTasksTable} alt="emptyTasksTable"/>
                <p style={{marginTop:'25px', fontWeight: 600, fontSize:'22px'}}>{translationsStore.translate('routePlanning.routesTable.noTaskYet')}</p>
              </div>
            }
            <div className="RouteTaskTablePagination">
              <div>{this.state.paginationText}</div>
              {this.props.routeTaskStore.routeTasksTotal > this.helperPageSize() && <Pagination {...this.state.pagination} />}
            </div>
            <EditRouteTask ref={this.EditRouteRef} width={560} />
            <AirModalTitlebar
              className="CantEditRouteTaskModal"
              width='550px'
              visible={this.state.visibleCantEdit}
              title={translationsStore.translate('routePlanning.editRoute.cantEditModal.title')}
              onCancel={() => this.setState({visibleCantEdit: false})}
              onOk={() => this.setState({visibleCantEdit: false})}
              okButton={<AirButton size="large" light label={'Ok'} />}
              buttonsStyle={{ justifyContent: 'center' }}
            />
          </Fragment>
        )}
      </div>
    );
  }
}

export default withWindowSize(RouteTaskTable);



// WEBPACK FOOTER //
// ./src/components/App/RouteTaskDashboard/RouteTaskTable/RouteTaskTable.js